import { Box } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { Text } from "#components/atoms/Typographies/Text";
import { Table } from "#components/organisms/Table/Table";
import { type RowAction } from "#components/organisms/Table/tableHook";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { invalidateQuoteRequestLinesDebounced } from "#modules/quoteRequestLines/infra/invalidateQuoteRequestLinesDebounced";
import { useBatchDisclaimerModal } from "#modules/quoteRequestLines/useBatchDisclaimerModal";
import { useDeleteQuoteRequestLineOrBatch } from "#modules/quoteRequestLines/view/useDeleteQuoteRequestLineOrBatch";
import { useDuplicateQuoteRequestLine } from "#modules/quoteRequestLines/view/useDuplicateQuoteRequestLine";
import {
  canDragQuoteRequestLineRow,
  getBatchGroupKey,
  getQuoteRequestLineRowDragGroupKey,
  isBatchSiblingQuoteRequestLineTableRow,
  isLastRowInBatchGroup,
  isLoadedQuoteRequestLineTableRow,
  LoadedQuoteRequestLineTableRow,
  QuoteRequestLineTableRow,
} from "#modules/quoteRequestLinesV2/domain/buildQuoteRequestLineTableRows";
import { QuoteRequestLineNameSortDirection } from "#modules/quoteRequestLinesV2/domain/sortQuoteRequestLinesByName";
import { AddBatchDisclaimerModal } from "#modules/quoteRequestLinesV2/view/organisms/AddBatchDisclaimerModal";
import { DeleteQuoteRequestLineConfirmationModal } from "#modules/quoteRequestLinesV2/view/organisms/DeleteQuoteRequestLineConfirmationModal";
import { EditPartModal } from "#modules/quoteRequestLinesV2/view/organisms/EditPartModal";
import { ImportPartsModal } from "#modules/quoteRequestLinesV2/view/organisms/ImportPartsModal";
import { QuoteRequestLinesBulkActionBar } from "#modules/quoteRequestLinesV2/view/organisms/QuoteRequestLinesBulkActionBar";
import { useQuoteRequestLinesSelection } from "#modules/quoteRequestLinesV2/view/useQuoteRequestLinesSelection";
import {
  PRICE_CELL_OVERLAY,
  useQuoteRequestLinesTableColumns,
} from "#modules/quoteRequestLinesV2/view/useQuoteRequestLinesTableColumns";
import { useQuoteRequestLineTableRows } from "#modules/quoteRequestLinesV2/view/useQuoteRequestLineTableRows";
import { QuoteRequestDto } from "#modules/quoteRequests/infra/fetchQuoteRequest.api";
import { useCheckPart } from "#modules/similarPart/view/useCheckPart";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import { MAX_BATCH_SIZE } from "shared";

const LOADING_ROWS_COUNT = 10;

const LOADING_ROWS = Array.from({ length: LOADING_ROWS_COUNT }, (_, index) => ({
  line: { id: `loading-${index}` },
  isLoadingRow: true as const,
}));

const QuoteRequestLinesEmptyState = ({ onImportClick }: { onImportClick: () => void }) => {
  const { t } = useTranslation();

  return (
    <Flex
      direction="column"
      align="center"
      gap={spacing[6]}
      style={{ padding: `${spacing[24]} ${spacing[12]}` }}
    >
      <Icon name="addFile" size={32} color={colors.icon.secondary} />
      <Flex direction="column" align="center" gap={spacing[2]}>
        <Text size="base" type="medium">
          {t("quoteRequestV2.emptyState.title")}
        </Text>
        <Text size="small" type="regular" color={colors.text.secondaryDefault}>
          {t("quoteRequestV2.emptyState.subtitle")}
        </Text>
      </Flex>
      <Button onClick={onImportClick}>{t("quoteRequestV2.emptyState.button")}</Button>
    </Flex>
  );
};

const QuoteRequestLinesSearchEmptyState = ({ searchTerm }: { searchTerm: string }) => {
  const { t } = useTranslation();

  return (
    <Flex
      direction="column"
      align="center"
      gap={spacing[6]}
      style={{ padding: `${spacing[24]} ${spacing[12]}` }}
    >
      <Icon name="search" size={32} color={colors.icon.secondary} />
      <Text size="base" type="medium">
        {t("quoteRequestV2.header.searchNoResults", { term: searchTerm })}
      </Text>
    </Flex>
  );
};

type Props = {
  quoteRequest: QuoteRequestDto | null;
  quoteRequestLines: (QuoteRequestLineDto | null | undefined)[];
  isInitialLoading: boolean;
  importModalOpened: boolean;
  onImportModalOpenedChange: (opened: boolean) => void;
  searchText?: string;
  sortDirection?: QuoteRequestLineNameSortDirection | null;
  onSortToggle?: () => void;
};

export const QuoteRequestLinesTable = ({
  quoteRequest,
  quoteRequestLines,
  isInitialLoading,
  importModalOpened,
  onImportModalOpenedChange,
  searchText = "",
  sortDirection,
  onSortToggle,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const isLoading = isInitialLoading || !quoteRequest;

  const [lineToDelete, setLineToDelete] = useState<LoadedQuoteRequestLineTableRow | null>(null);
  const [lineToEdit, setLineToEdit] = useState<QuoteRequestLineDto | null>(null);
  const { deleteQuoteRequestLineOrBatch, isPending: isDeletePending } =
    useDeleteQuoteRequestLineOrBatch({
      onSuccess: () => setLineToDelete(null),
    });

  const { duplicateQuoteRequestLine } = useDuplicateQuoteRequestLine();
  const { shouldShowModal, modalOpened, openModal, closeModal, handleConfirm, checkboxProps } =
    useBatchDisclaimerModal();
  const [lineToAddBatchTo, setLineToAddBatchTo] = useState<QuoteRequestLineDto | null>(null);

  const { checkPart: reanalyzePart } = useCheckPart();

  const handleReanalyze = (row: QuoteRequestLineTableRow) => {
    if (!isLoadedQuoteRequestLineTableRow(row)) {
      return;
    }

    const quoteRequestLineId = row.line.id;

    reanalyzePart(
      { id: row.line.part.id, force: true, quoteRequestLineId },
      {
        onSuccess: () => {
          invalidateQuoteRequestLinesDebounced({ queryClient, quoteRequestLineId });
        },
      },
    );
  };

  const loadedQuoteRequestLines = useMemo(
    () => quoteRequestLines.filter((line): line is QuoteRequestLineDto => Boolean(line)),
    [quoteRequestLines],
  );

  const {
    selectedCount,
    hasAnalyzingSelectedLine,
    rowSelection,
    indeterminateRowIds,
    handleRowSelectionChange,
    clearSelection,
    selectedIds,
  } = useQuoteRequestLinesSelection(loadedQuoteRequestLines);

  const {
    rows,
    isSearching,
    expandedAssemblyIds,
    toggleAssemblyCollapse,
    handleDragStart,
    handleDragSettled,
    handleRowOrderChange,
  } = useQuoteRequestLineTableRows(quoteRequest?.id, quoteRequestLines, searchText);

  const handleAddBatchClick = (row: QuoteRequestLineTableRow) => {
    if (!isLoadedQuoteRequestLineTableRow(row) || row.depth > 0) {
      return;
    }
    const isAlreadyABatch = row.assembliesInBatch.length > 1;
    setLineToAddBatchTo(row.line);
    if (shouldShowModal(isAlreadyABatch)) {
      openModal();
    } else {
      duplicateQuoteRequestLine({ id: row.line.id, useBatch: true });
    }
  };

  const rowActions: RowAction<QuoteRequestLineTableRow>[] = [
    {
      label: t("quoteRequestLine.actions.edit"),
      icon: <Icon name="pencil" size={16} />,
      onClick: (row) => (isLoadedQuoteRequestLineTableRow(row) ? setLineToEdit(row.line) : null),
      disabled: (row) => !isLoadedQuoteRequestLineTableRow(row),
    },
    {
      label: t("quoteRequestLine.actions.addBatch"),
      icon: <Icon name="plus" size={16} />,
      onClick: handleAddBatchClick,
      hidden: (row) => isLoadedQuoteRequestLineTableRow(row) && row.depth > 0,
      disabled: (row) =>
        !isLoadedQuoteRequestLineTableRow(row) ||
        row.depth > 0 ||
        row.assembliesInBatch.length >= MAX_BATCH_SIZE,
      disabledTooltip: (row) =>
        isLoadedQuoteRequestLineTableRow(row) && row.assembliesInBatch.length >= MAX_BATCH_SIZE
          ? t("quoteRequestLine.actions.addBatchMaxReachedTooltip", { max: MAX_BATCH_SIZE })
          : undefined,
    },
    {
      label: t("quoteRequestLine.actions.reanalyze"),
      icon: <Icon name="rotateRight" size={16} />,
      onClick: handleReanalyze,
      disabled: (row) => !isLoadedQuoteRequestLineTableRow(row) || row.isAnalyzingOrPredicting,
    },
    {
      label: t("common.delete"),
      icon: <Icon name="trash" size={16} color={colors.icon.errorDefault} />,
      variant: "destructive",
      onClick: (row) => (isLoadedQuoteRequestLineTableRow(row) ? setLineToDelete(row) : null),
      disabled: (row) => !isLoadedQuoteRequestLineTableRow(row),
    },
  ];

  const columns = useQuoteRequestLinesTableColumns(
    expandedAssemblyIds,
    toggleAssemblyCollapse,
    sortDirection,
    onSortToggle,
    setLineToEdit,
  );

  return (
    <>
      <Box
        style={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Table
          columns={columns}
          data={isLoading ? LOADING_ROWS : rows}
          enableSorting={false}
          getRowId={(row) => row.line.id}
          loading={isLoading}
          rowActions={isLoading ? undefined : rowActions}
          rowActionsCellOverlay={PRICE_CELL_OVERLAY}
          enableRowSelection={(row) => !isBatchSiblingQuoteRequestLineTableRow(row)}
          rowSelection={rowSelection}
          indeterminateRowIds={indeterminateRowIds}
          onRowSelectionChange={handleRowSelectionChange}
          enableDnd={!isLoading}
          isDndColumnInGutter
          getRowDragGroupKey={getQuoteRequestLineRowDragGroupKey}
          canDragRow={canDragQuoteRequestLineRow(isSearching)}
          dragHandleLabel={t("quoteRequestV2.dragHandle.tooltip")}
          onDragStart={handleDragStart}
          onDragSettled={handleDragSettled}
          onRowOrderChange={handleRowOrderChange}
          getRowHeight={(row) => (getBatchGroupKey(row) !== undefined ? "48px" : "72px")}
          isRowDividerVisible={isLastRowInBatchGroup}
          getRowExtraTopPadding={(row) =>
            getBatchGroupKey(row) !== undefined && isLoadedQuoteRequestLineTableRow(row)
              ? spacing[3]
              : undefined
          }
          getRowExtraBottomPadding={(row) =>
            getBatchGroupKey(row) !== undefined && isLastRowInBatchGroup(row)
              ? spacing[3]
              : undefined
          }
          getRowGroupKey={getBatchGroupKey}
          emptyState={
            isSearching ? (
              <QuoteRequestLinesSearchEmptyState searchTerm={searchText} />
            ) : (
              <QuoteRequestLinesEmptyState onImportClick={() => onImportModalOpenedChange(true)} />
            )
          }
        />
        {selectedCount > 0 && (
          <Box
            style={{
              position: "absolute",
              bottom: spacing[6],
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
              width: "max-content",
            }}
          >
            <QuoteRequestLinesBulkActionBar
              selectedCount={selectedCount}
              hasAnalyzingSelectedLine={hasAnalyzingSelectedLine}
              onClearSelection={clearSelection}
              selectedIds={selectedIds}
            />
          </Box>
        )}
      </Box>
      <DeleteQuoteRequestLineConfirmationModal
        opened={lineToDelete !== null}
        onClose={() => setLineToDelete(null)}
        onConfirm={() => {
          if (lineToDelete) {
            const idsInBatch = lineToDelete.assembliesInBatch.map((a) => a.quoteRequestLine.id);
            deleteQuoteRequestLineOrBatch(idsInBatch, lineToDelete.line.id);
          }
        }}
        loading={isDeletePending}
      />
      <AddBatchDisclaimerModal
        opened={modalOpened}
        onClose={() => {
          closeModal();
          setLineToAddBatchTo(null);
        }}
        onConfirm={() =>
          handleConfirm(() => {
            if (lineToAddBatchTo) {
              duplicateQuoteRequestLine({
                id: lineToAddBatchTo.id,
                useBatch: true,
              });
            }
          })
        }
        checkboxProps={checkboxProps}
      />
      {quoteRequest && (
        <ImportPartsModal
          opened={importModalOpened}
          onClose={() => onImportModalOpenedChange(false)}
          quoteRequest={quoteRequest}
        />
      )}
      {lineToEdit && <EditPartModal line={lineToEdit} onClose={() => setLineToEdit(null)} />}
    </>
  );
};
